/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable max-lines */
/* eslint-env browser */
/* eslint-disable max-statements */

// TODO Refaktorovat
// - Nabízí se vyčlenění metod, které operují nad FileInfo (např. listFile)
//   a ne nad Asset.
// - Samostatný generátor identifikátorů.
// - Rozdělit na dvě třídy pro stahování souborů a pro stahování archivů.
//   Na začátku se dle extract rozhodnout, který použít.
//   Případně extract vůbec do této třídy nepředávat
//   a rozhodnutí provést ve vyšší třídě např. ViewerType#getDownloader.
//   Aktuální řešení s `if (extract) {...}` není ideální.

import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import objectHash from 'object-hash';
import PathBrowserify from 'path-browserify';

import ErrorName from '../../error/error-name';
import { errorWithLevelWarning } from '../../error/error-with-level';
import FileSystemPathFactory from '../../file/system/file-system-path-factory';
import { isISOString, toISOString } from '../../iso-string';
import { getUri } from '../../medium/medium';
import PromiseUtil from '../../promise/promise-util';
import { parseURL } from '../../url';

/**
 * @import BasePathResolver from '../../basepath/basepath'
 * @import { FileArchiveManager } from '../../file/archive'
 * @import { FileSystemDirectory, FileSystemEntry, FileSystemFile, FileSystemManager } from '../../file/system'
 * @import FileSystemPath from '../../file/system/file-system-path'
 * @import { Medium, URI } from '../../medium/medium'
 * @import { Logger } from '../../logger/logger'
 * @import Store from '../../store/store'
 * @import { Asset } from '../asset'
 * @import { AssetAttrs } from '../asset-attrs'
 * @import Downloader from '../../downloader/downloader'
 */

// TODO #471 Doc: Upravit poznámky, že se checksum používá jen pro legacy assety.
// TODO Přejmenovat na AssetManager? AssetCache? AssetCacher? FileTransferCache?
// - Ještě asi nastudovat nějaký oficiální algoritmus pro cache, jestli se na něco nezapomnělo.
// - https://developer.mozilla.org/en-US/docs/Web/API/Cache
// TODO Občas může dojít ke zbytečnému stažení souboru při vypnuté cache.
// - Podmínkou je, aby se soubor změnil na verzi, ve které už byl.
//   Např. z jedné URL se stáhne soubor 1, potom soubor 2 a nakonec znovu 1.
//   V tom okamžiku jsou v cache oba soubory, ale přesto se soubor 1 znovu stáhne.
//   Důvodem je, že v If-None-Match se posílá jen poslední etag.
//   Řešením by bylo posílat v něm všechny známé ETag a pak z výsledku převzít ETag, který se má použít.
//   V hlavičcce If-Modified-Since by se pravděpodobně mělo posílat poslední datum Last-Modified.
// - Zatím toto není ošetřeno, protože se takto cache většinou nepoužívá.
//   Důsledky také nejsou kritické, jen se provede zbytečné stažení souboru,
//   který se následně smaže, jinak vše funguje.
// TODO V ideálním případě by asi měl AssetDownloader implementovat FileTransferManager
// - Nebo alespoň část AssetDownloader po jeho refaktorování.
// - V podstatě by se k jeho funkcionalitě přidávalo cachování,
//   jinak by se API shodovalo.
// - Ale je zde řada problémů, např. co s extract?
//   Všechny cache fungují na základě cachování souborů, nikoliv složek.
//   Také je zbytečné cachovat stažený archiv a vedle něj mít rozbalenou složku.
// - Dále se cache většinou zakládají nejen na obsahu URL,
//   ale také na obsahu hlaviček a typu HTTP metody.
// - Dále se z cache vrací celý objekt Response, ne jen cesta k souboru.
/**
 * Stahovač a organizér záznamů v souborovém systému
 *
 * Využívá obecné rozhraní ke stahování souborů a jejich organizaci na disku.
 * Funguje jako cache pro stahované soubory a opakované požadavky na stažení
 * ze stejné URL může vracet z této cache bez odeslání požadavku na server.
 * Spravuje metadata o uložených souborech.
 * Provádí operace nad soubory podle nastavení jako je `cache`
 * (kontrola aktuální verze) nebo `extract`
 * (rozbalení souborů s archivy do složky).
 */
export default class AssetDownloader {
  /**
   * @param {BasePathResolver} basePathResolver
   * @param {Downloader} downloader
   * @param {FileArchiveManager} fileArchiveManager Správce archivů.
   * @param {FileSystemManager} fileSystemManager Správce souborového systému.
   * @param {Logger} logger
   * @param {Store} store Perzistentní read-write datový sklad s assety.
   */
  constructor(
    basePathResolver,
    downloader,
    fileArchiveManager,
    fileSystemManager,
    logger,
    store
  ) {
    this.basePathResolver = basePathResolver;
    this.downloader = downloader;
    this.fileArchiveManager = fileArchiveManager;
    this.fileSystemManager = fileSystemManager;
    this.logger = logger;
    this.store = store;
    // TODO: Předělat za const na úrovni souboru.
    /**
     * Přípona souboru, která označuje dočasný soubor.
     * Používá se při stahování, aby šlo kompletně nedostahované soubory
     * identifikovat a smazat.
     * Pří úspěšném stažení se soubor přesune do finálního umístění
     * bez této přípony.
     * @private
     * @type {string}
     */
    this.temporaryExtension = 'tmp';
  }

  /**
   * Zkontroluje, zda jsou assety v localStorage validní (nevalidní smaže) nebo
   * "zvalidovatelné" (tj. asset má všechny povinné položky typově v pořádku,
   * ale některé volitelné jsou undefined místo null, jak předepisuje typ),
   * které případně perzistentně namigruje.
   *
   * Případná chyba zpracování jednoho assetu nezpůsobí přerušení migrace ostatních.
   */
  async migrateAssets() {
    // Zde je jediné místo v celé aplikaci, kde se z localStorage může načíst
    // typ MaybeAsset a právě tato funkce zajištuje migraci na Asset pro ostatní
    // kód, který musí být volaný zaručeně vždy až po dokončení migrateAssets.
    /** @type {MaybeAsset[]} */
    const maybeAssets = await this.store.findAllAsset();
    const migrationFunctions = maybeAssets.map((maybeAsset) => async () => {
      // Všechny nevalidní assety, které byly v localStorage se smažou.

      // Případné uložení dirty "zvalidněného" assetu se děje níže až
      // po ověření existence odkazovaného souboru/adresáře, jinak by
      // se asset zbytečně ukládal, když by se vzápětí mohl smazat.
      if (!assetMakeValid(maybeAsset)) {
        this.logger.warn(
          new TypeError(`invalid asset: ${JSON.stringify(maybeAsset)}`)
        );
        await this.destroyAssetSilently(maybeAsset.id);
        return;
      }
      const asset = /** @type {Asset} */ (maybeAsset);
      if (this.store.hasChangesAsset(asset.id)) {
        // Uložení změn po případné migraci v assetMakeValid.
        await this.store
          .saveAsset(asset.id)
          .catch(() => this.destroyAssetSilently(asset.id));
      }
    });
    // Migrace assetů.
    await PromiseUtil.allSerial(migrationFunctions);
  }

  /**
   * Zkontroluje přítopnost assetů v úložišti. Assety, jejichž logická path neukazuje
   * na existující fyzický soubor/adresář, smaže.
   *
   * Případná chyba zpracování jednoho assetu nezpůsobí přerušení kontroly ostatních.
   * @returns {Promise<void>}
   */
  async checkAssets() {
    const assets = await /** @type {Promise<Asset[]>} */ (
      this.store.findAllAsset()
    );
    const checkAssetFunctions = assets.map(
      (/** @type {Asset} */ asset) => async () => {
        const logicalPath = FileSystemPathFactory.fromValue(asset.path);

        // Kontrola, že asset existuje a existuje soubor, na který ukazuje.
        /** @type {FileSystemPath | undefined} */
        const physicalPath = await this.basePathResolver
          .toPhysicalPath(logicalPath)
          .catch(() => undefined);
        if (isNil(physicalPath)) {
          return await this.destroyAssetSilently(asset.id);
        }

        const exists = await /** @type {Promise<boolean>} */ (
          this.fileSystemManager.hasEntry(physicalPath)
        );

        if (!exists) {
          // Pokud soubor neexistuje, pak se daný asset smaže.
          return await this.destroyAssetSilently(asset.id);
        }
      }
    );
    await PromiseUtil.allSerial(checkAssetFunctions);
  }

  /**
   * Dokončí vytvoření nového záznamu po jeho stažení.
   * @param {FileSystemPath} baseLogicalPath Logická cesta ke složce, kde se ukládají záznamy.
   * @param {FileSystemPath} basePhysicalPath Fyzická cesta ke složce, kde se ukládají záznamy.
   * @param {string} extension
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {Response} response
   * @param {FileSystemPath} tempDirPhysicalPath
   * @param {FileSystemFile} tempPhysicalFile
   * @param {URI} uri
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum]
   * @private
   * @returns {Promise<Asset>}
   */
  async createDownloaded(
    baseLogicalPath,
    basePhysicalPath,
    extension,
    extract,
    response,
    tempDirPhysicalPath,
    tempPhysicalFile,
    uri,
    { checksum } = {}
  ) {
    const lastFetched = new Date();
    const { headers } = response;
    const etag = this.parseEtag(headers);
    const lastModified = parseLastModified(headers);
    const targetId = AssetDownloader.generateId(uri, {
      etag,
      lastFetched,
      lastModified
    });
    const targetPhysicalPath = this.parseTargetPath(
      basePhysicalPath,
      extension,
      extract,
      targetId
    );
    await this.createTarget(
      extract,
      targetPhysicalPath,
      tempDirPhysicalPath,
      tempPhysicalFile
    );
    const targetLogicalPath = this.parseTargetPath(
      baseLogicalPath,
      extension,
      extract,
      targetId
    );
    return await this.createAsset(targetId, targetLogicalPath, uri, {
      checksum,
      etag,
      lastFetched,
      lastModified
    });
  }

  /**
   * Rozbalí stažený archiv a vytvoří z něj cílovou složku.
   * Pokud zde již cílová složka je, tak se archiv smaže.
   * @param {FileSystemPath} targetPhysicalPath
   * @param {FileSystemPath} tempDirPhysicalPath
   * @param {FileSystemFile} tempPhysicalFile
   * @private
   * @returns {Promise<FileSystemEntry>}
   */
  createArchive(targetPhysicalPath, tempDirPhysicalPath, tempPhysicalFile) {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getDirectory(targetPhysicalPath).then(
          (targetPhysicalDirectory) =>
            tempPhysicalFile.remove().then(() => targetPhysicalDirectory),
          (error) => {
            if (error.name === ErrorName.NotFoundError) {
              return this.fileSystemManager
                .createDirectory(tempDirPhysicalPath)
                .then((temporaryPhysicalDirectory) =>
                  this.extractArchive(
                    targetPhysicalPath,
                    temporaryPhysicalDirectory,
                    tempPhysicalFile
                  ).catch((extractError) =>
                    temporaryPhysicalDirectory
                      .remove({ recursive: true })
                      .then(() => {
                        throw extractError;
                      })
                  )
                );
            }
            throw error;
          }
        )
      )
    );
  }

  /**
   * Vytvoří metadata záznamu a uloží je do perzistentního úložiště.
   * @param {string} id The primary key of the item to generate.
   * @param {FileSystemPath} logicalPath Logical path to the item's entry.
   * @param {URI} uri
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum]
   * @param {string} [opts.etag]
   * @param {Date} [opts.lastFetched]
   * @param {Date} [opts.lastModified]
   * @private
   * @returns {Promise<Asset>}
   */
  createAsset(
    id,
    logicalPath,
    uri,
    { checksum, etag, lastFetched, lastModified } = {}
  ) {
    return this.store.createAsset(
      {
        checksum: checksum ?? null,
        etag: etag ?? null,
        lastFetched: isNil(lastFetched) ? null : toISOString(lastFetched),
        id,
        lastModified: isNil(lastModified) ? null : toISOString(lastModified),
        path: logicalPath.getValue(),
        // Kvůli zpětné kompatibilitě. URL je nově podepsaná, URI je v původním formátu URL.
        url: uri
      },
      {
        // Přepsat celý existující záznam.
        onConflict: 'replace',
        // Pokud již záznam existuje, aktualizuje se.
        upsert: true
      }
    );
  }

  /**
   * Přesune stažený soubor na cílový soubor.
   * Pokud zde již cílový soubor je, tak se stažený soubor smaže.
   * @param {FileSystemPath} targetPhysicalPath
   * @param {FileSystemFile} tempPhysicalFile
   * @private
   * @returns {Promise<FileSystemEntry>}
   */
  createFile(targetPhysicalPath, tempPhysicalFile) {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getFile(targetPhysicalPath).then(
          (targetFile) => tempPhysicalFile.remove().then(() => targetFile),
          (error) => {
            if (error.name === ErrorName.NotFoundError) {
              return tempPhysicalFile.move(targetPhysicalPath);
            }
            throw error;
          }
        )
      )
    );
  }

  /**
   * Vytvoří fyzický cílový záznam ze staženého souboru.
   * Pokud zde již cílový záznam je, tak se stažený soubor smaže.
   * Bohužel nejde před začátkem stahování detekovat, že byl soubor již stažený.
   * To lze zjistit až z odpovědi na stažení souboru (ETag, Last-Modified).
   * Stejný hash v ID by měl zaručit, že je to stejný soubor.
   * Smazání aktuálního souboru je nebezpečné (může být používaný),
   * přesun a rozbalování je zbytečné.
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {FileSystemPath} targetPhysicalPath
   * @param {FileSystemPath} tempDirectoryPhysicalPath
   * @param {FileSystemFile} tempPhysicalFile
   * @private
   * @returns {Promise<FileSystemEntry>} Fyzický cílový záznam.
   */
  createTarget(
    extract,
    targetPhysicalPath,
    tempDirectoryPhysicalPath,
    tempPhysicalFile
  ) {
    return new Promise((resolve) => {
      if (extract) {
        return resolve(
          this.createArchive(
            targetPhysicalPath,
            tempDirectoryPhysicalPath,
            tempPhysicalFile
          )
        );
      }
      // Soubory se do cílového umístění přesunují.
      return resolve(this.createFile(targetPhysicalPath, tempPhysicalFile));
    });
  }

  /**
   * Smaže cachovaný záznam a jeho metadata.
   * @param {string} id Identifikátor záznamu, který se má smazat.
   * @returns {Promise<void>}
   */
  async destroy(id) {
    const { store } = this;
    const asset = await store.findAsset(id);
    if (isNil(asset)) {
      return;
    }
    const logicalPath = FileSystemPathFactory.fromValue(asset.path);
    const physicalPath = await this.basePathResolver.toPhysicalPath(
      logicalPath
    );
    const entry = await this.fileSystemManager.getEntry(physicalPath);
    // Použije se smazání s recursive, protože záznamem může být i neprázdná složka.
    await entry.remove({ recursive: true });
    await store.destroyAsset(id);
  }

  /**
   * Smaže asset s případným zalogováním chyby jako warning,
   * vždy se vyřeší a nikdy neodmítne.
   * @private
   * @param {AssetAttrs['id']} assetId
   * @returns {Promise<void, never>}
   */
  async destroyAssetSilently(assetId) {
    try {
      await this.store.destroyAsset(assetId);
    } catch (error) {
      this.logger.warn(error);
    }
  }

  /**
   * Stáhne nový záznam ze zadané URL.
   * Podporuje cachování, aby se soubory zbytečně opakovaně nestahovaly.
   *
   * Zdroje:
   * - [RequestCache WHATWG](https://fetch.spec.whatwg.org/#concept-request-cache-mode)
   * - [RequestCache MDN](https://developer.mozilla.org/en-US/docs/Web/API/Request/cache)
   * @param {FileSystemPath} baseLogicalPath Cesta ke složce, kde se ukládají záznamy.
   * @param {Medium} medium Stahované medium.
   * @param {Object} [opts={}]
   * @param {boolean} [opts.extract] Příznak, jestli se má stažený soubor
   * rozbalit do složky.
   * @returns {Promise<Asset>}
   */
  async download(baseLogicalPath, medium, { extract = false } = {}) {
    const headers = getHeadersForMedium(medium);
    const uri = getUri(medium);
    // Převod URL na objekt, což provede normalizaci URL a ulehčí přístup
    // k jejím částem.
    const url = parseURL(medium.url);

    const { checksum } = medium;
    // Zjištění názvu souboru, validace a parsování jeho přípony.
    // Určuje se předem, aby se zbytečně nestahovalo,
    // pokud by přípona nebyla validní.
    const extension = this.parseExtension(url);
    const basePhysicalPath = await this.basePathResolver.toPhysicalPath(
      baseLogicalPath
    );

    const tempDirPhysicalPath = this.parseTempDirPhysicalPath(
      basePhysicalPath,
      uri
    );

    const tempFilePhysicalPath = this.parseTempFilePhysicalPath(
      basePhysicalPath,
      uri
    );

    try {
      await this.fileSystemManager.createDirectory(basePhysicalPath);
    } catch (error) {
      // Pokud již pracovní složka existuje, nevadí to.
      // TODO Přidat do createDirectory příznak pro tolerování existující složky.
      // - Nyní nelze atomickým dotazem rozeznat, jestli je na cestě složka nebo soubor.

      if (/** @type {Error} */ (error).name !== ErrorName.PathExistsError) {
        throw error;
      }
    }

    // Odstranění dočasného souborů z případného předchozího běhu.
    await this.removePhysicalFile(tempFilePhysicalPath).catch((error) => {
      if (error.name !== ErrorName.NotFoundError) {
        throw error;
      }
    });
    try {
      const { file, response } = await this.downloader.download(
        tempFilePhysicalPath,
        url.toString(),
        { headers }
      );

      if (!response.ok) {
        throw errorWithLevelWarning(
          new RangeError(`Invalid HTTP status "${response.status}".`)
        );
      }
      if (!file) {
        throw errorWithLevelWarning(new Error('File without content.'));
      }
      // TODO: Zdokumentovat, že se ošetření výsledku stažení (i neúspěchu) děje až v createDownloaded.
      return await this.createDownloaded(
        baseLogicalPath,
        basePhysicalPath,
        extension,
        extract,
        response,
        tempDirPhysicalPath,
        // TODO: Mělo by být pro zřejmost tempFilePhysicalPath, pokud je to stejné jako file.
        file,
        uri,
        // TODO: Zde checksum je, ale v update ne a tam zase jsou etag a lastModified a zde ne.
        { checksum }
      )
        // Ošetření selhání v operacích po stažení souboru.
        .catch((error) =>
          // Pokud soubor nebyl stažen, nic se nedělá.
          // TODO: Nemůže při network chybě vzniknout soubor a pak přijít NetworkError?
          // Pak by se i v tomto případě měl smazat prázdný/rozestahovaný soubor.
          // TODO: Mělo by být pro zřejmost tempFilePhysicalPath, pokud je to stejné jako file.
          // Pokud byl soubor stažen, smaže se.
          file?.remove().then(() => {
            throw error;
          })
        );
    } catch (error) {
      // Odstranění dočasného souboru při výskytu chyby.
      await this.removePhysicalFile(tempFilePhysicalPath).catch(() => {});

      if (/** @type {Error} */ (error).name === ErrorName.NetworkError) {
        throw errorWithLevelWarning(/** @type {Error} */ (error));
      }
      throw error;
    }
  }

  /**
   * Rozbalí archiv do složky a smaže ho.
   * @param {FileSystemPath} targetPhysicalPath
   * @param {FileSystemDirectory} tempPhysicalDirectory
   * @param {FileSystemFile} tempPhysicalFile
   * @private
   * @returns {Promise<FileSystemEntry>}
   */
  extractArchive(targetPhysicalPath, tempPhysicalDirectory, tempPhysicalFile) {
    return new Promise((resolve) =>
      resolve(
        this.fileArchiveManager
          .getVolume(tempPhysicalFile.getPath())
          .then((volume) => volume.extract(tempPhysicalDirectory.getPath()))
          .then(() => tempPhysicalFile.remove())
          .then(() => tempPhysicalDirectory.move(targetPhysicalPath))
      )
    );
  }

  /**
   * TODO #471 Doc
   * @param {Store} store
   * @param {URI} uri
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum] Kontrolní součet, pod kterým by mohl být
   * záznam uložen.
   * @returns {Promise<Asset | undefined>}
   */
  static async findAsset(store, uri, { checksum } = {}) {
    if (isNil(checksum)) {
      return (
        (await AssetDownloader.findLatestAsset(store, uri)) ??
        AssetDownloader.findLegacyAsset(store, uri)
      );
    }
    return (
      (await AssetDownloader.findAssetByChecksum(store, checksum)) ??
      AssetDownloader.findLegacyAsset(store, uri, { checksum })
    );
  }

  // TODO: Zavést sekundární index na property checksum.
  /**
   * TODO #471 Doc
   * @param {Store} store
   * @param {string} checksum Kontrolní součet, pod kterým je
   * záznam uložen.
   * @private
   * @returns {Promise<Asset | undefined>}
   */
  static async findAssetByChecksum(store, checksum) {
    const assets = await store.findAllAsset();
    return assets.find((asset) => asset.checksum === checksum);
  }

  /**
   * Vyhledá nejnovější záznam, který byl stažený ze zadané URI.
   * @param {Store} store
   * @param {URI} uri
   * @private
   * @returns {Promise<Asset | undefined>}
   */
  static async findLatestAsset(store, uri) {
    const assets = await store.findAllAsset();
    // Kvůli zpětné kompatibilitě. URL je nově podepsaná, URI je v původním formátu URL.
    const matchingAssets = assets.filter((asset) => asset.url === uri);
    if (!matchingAssets.length) {
      return;
    }
    // TODO Porovnávat i dle identifikátoru:
    // - V případě, že jsou oba datumy NaN, nebo mají shodnou hodnotu.
    // - Vzhledem s sériovému stahování, je ale skoro nemožné,
    //   aby byl fetch odlišný, obzvláště u dvou verzí jednoho média.
    return matchingAssets.reduce((acc, asset) => {
      // Datum stažení assetu, přes který se iteruje.
      //
      // Klauzule `?? ''` zajišťuje Invalid Date (`valueOf() === NaN`)
      // i pro nullish (null | undefined) vstup. Bohužel spoléhá na to,
      // že prázdný řetězec (jeden z povolených typů string | number | Date)
      // se považuje za nevalidní datum, což je ale není předepsané
      // ve specifikaci, ale je implementačně specifické (např. v Chrome/Edge
      // null na vstupu vrací `valueOf() === 0` - UNIX epoch, ale pro
      // `undefined` se vrací `valueOf() === NaN`).
      const lastFetched = new Date(asset.lastFetched ?? '');
      // Asset musí mít vyplněné datum stažení, jinak se použije předchozí.
      if (isNaN(lastFetched.valueOf())) {
        return acc;
      }
      const accLastFetched = new Date(acc.lastFetched ?? '');
      // Pokud akumulátor nemá datum, nebo je starší než iterovaný,
      // tak se použije iterovaný.
      if (isNaN(accLastFetched.valueOf()) || lastFetched > accLastFetched) {
        return asset;
      }
      // Iterovaný není novější než akumulátor, použije se akumulátor.
      return acc;
    });
  }

  /**
   * Vyhledá záznam stažený ze zadané URI podle starého algoritmu.
   * Slouží k vyhledání souborů uložených v předchozí verzi algoritmu.
   * V této verzi každé URI odpovídal právě jeden cílový soubor, který měl ID
   * dle parametru checksum, případně vytvořené zahashováním URI.
   * @param {Store} store
   * @param {URI} uri
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum] Kontrolní součet, pod kterým by mohl být
   * záznam uložen.
   * @private
   * @returns {Promise<Asset | undefined>}
   */
  static async findLegacyAsset(store, uri, { checksum } = {}) {
    // Určení identifikátoru nového souboru.
    const id = AssetDownloader.generateId(uri, { checksum });
    // Vyhledání souboru, který patří k médiu.
    const asset = await store.findAsset(id);
    return asset;
  }

  /**
   * Vygeneruje identifikátor záznamu.
   *
   * Identifikátor se vždy generuje pouze z jedné dodatečné informace k URL.
   * To je z důvodu, když by se změnila např. hodnota Last-Modified,
   * ale byl by zachován stejný ETag, tak není změna ID očekávaná.
   * Generuje se podle následujících priorit:
   *
   * 1. `checksum` ze staré verze algoritmu se přebírá jako ID.
   * 2. `etag` je primární zdroj informace o změně média.
   * 3. `lastModified` se použije, pokud server neposlal ETag.
   * 4. `lastFetched` se použije, pokud server neposlal ani Last-Modified.
   * 5. `url` se použije, pokud není dostupné nic jiného, stejně jako
   *    ve staré verzi algoritmu.
   *
   * Identifikátor se generuje proto, aby se odstranily problémy
   * se speciálními znaky v názvu souboru a délkou jeho názvu.
   * Zároveň to umožní k jedné URL ukládat více verzí souborů,
   * které z ní byly stažené.
   * @param {URI} uri URI adresa stahovaného media pro který se generuje identifikátor.
   * @param {Object} [opts={}]
   * @param {string} [opts.checksum] Kontrolní součet, pod kterým by mohl být
   * záznam uložen podle starého algoritmu.
   * @param {string} [opts.etag] ETag záznamu.
   * @param {number} [opts.index = 0] Pořadí vygenerovaného identifikátoru,
   * které umožňuje k jedné sadě informací identifikující záznam vytvořit
   * neomezené množství identifikátoru např. pro uložení více dočasných souborů.
   * @param {Date} [opts.lastFetched] Datum posledního ověření platnosti záznamu.
   * @param {Date} [opts.lastModified] Datum poslední změny záznamu na serveru.
   * @private
   * @returns {string}
   */
  static generateId(
    uri,
    { checksum, etag, index = 0, lastFetched, lastModified } = {}
  ) {
    if (!isNil(checksum)) {
      return checksum;
    }
    if (!isNil(etag)) {
      return objectHash({
        etag,
        index,
        url: uri
      });
    }
    if (!isNil(lastModified)) {
      return objectHash({
        index,
        lastModified: toISOString(lastModified),
        url: uri
      });
    }
    if (!isNil(lastFetched)) {
      return objectHash({
        index,
        lastFetched: toISOString(lastFetched),
        url: uri
      });
    }
    if (index !== 0) {
      return objectHash({
        index,
        url: uri
      });
    }
    // Výchozím zdrojem identifikátoru je URL, na které je uložený binární obsah.
    // Na URL se ještě provede hash.
    // To odstraní speciální znaky o omezí délku pojmenování souboru.
    return objectHash(uri);
  }

  // TODO: Listují se jen soubory nebo i složky (např. pro widgety)?
  // Podle toho by se pak měl patřičně upravit název, ať není zavádějící...
  // TODO: Veřejné funkce by měly mít defaultně jen a pouze logické cesty!
  // Ale používá se to v cleaneru, kde se stejně provádí mapování z logických na fyzické, tak by to možná šlo změnit.
  /**
   * Lists all files with detailed information for a requested resource.
   * @param {FileSystemPath} basePhysicalPath Fyzická cesta ke složce, kde se ukládají záznamy.
   * @returns {Promise<FileInfo[]>} List of infos about files.
   */
  listFile(basePhysicalPath) {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager.getDirectory(basePhysicalPath).then(
          (directory) =>
            directory
              .list()
              .then((entryArray) =>
                entryArray.map((entry) => this.parseFile(entry))
              ),
          (error) => {
            // Pokud složka s workspace neexistuje, považuje se za prázdnou.
            if (error.name === ErrorName.NotFoundError) {
              return [];
            }
            throw error;
          }
        )
      )
    );
  }

  /**
   * Načte hodnotu ETagu z HTTP hlaviček.
   * @param {Headers} headers
   * @private
   * @returns {string|undefined}
   */
  parseEtag(headers) {
    return headers.get('ETag') ?? undefined;
  }

  /**
   * Načte příponu souboru z URL, ze které se stahuje.
   * Algoritmus se snaží příponu ve staženém souboru zachovat.
   *
   * URL s příponou, která je shodná s dočasnou, se zablokuje.
   * To je problém, protože se nejprve vytvoří `${id}.tmp.tmp`, což nevadí,
   * ale po stažení se přesune na `${id}.tmp`, což je cesta, která označuje
   * dočasný soubor, které jsou při každém spuštění zařízení smazány.
   * @param {URL} url URL adresa, ze které se má zjistit přípona.
   * @private
   * @returns {string}
   */
  parseExtension(url) {
    const extension = PathBrowserify.extname(url.pathname).slice(1);
    if (extension === this.temporaryExtension) {
      throw new TypeError(`Invalid URL extension "${extension}".`);
    }
    return extension;
  }

  /**
   * Parses an information about a file.
   * @param {FileSystemEntry} physicalEntry File's entry in the file system.
   * @private
   * @returns {FileInfo} Info about the file.
   */
  parseFile(physicalEntry) {
    const path = physicalEntry.getPath();
    return {
      entry: physicalEntry,
      id: path.getStem(),
      temporary: path.getExtension() === this.temporaryExtension
    };
  }

  /**
   * Načte cílovou cestu (logickou nebo fyzickou), na které by měl být uložený záznam.
   * @param {FileSystemPath} basePath Cesta ke složce (fyzické nebo logické), kde se ukládají záznamy.
   * @param {string} extension
   * @param {boolean} extract Příznak, jestli stažený soubor rozbalit do složky.
   * @param {string} id Identifikátor záznamu, jehož cesta se načítá.
   * @private
   * @returns {FileSystemPath}
   */
  parseTargetPath(basePath, extension, extract, id) {
    // Název cílového vytvořeného souboru.
    let targetFilePath;
    if (extract) {
      // Archivy se rozbalují do složky pojmenované dle id.
      targetFilePath = FileSystemPathFactory.fromInfo({ stem: id });
    } else {
      // Soubory se pojmenovávají dle id a zachovává se přípona.
      targetFilePath = FileSystemPathFactory.fromInfo({ extension, stem: id });
    }
    // Cesta k vytvořenému souboru.
    return FileSystemPathFactory.join(basePath, targetFilePath);
  }

  /**
   * Určí na jakou dočasnou fyzickou cestu uložit záznam.
   * @param {FileSystemPath} physicalPath Fyzická cesta.
   * @param {string} id Identifikátor záznamu, pro který se určuje fyzická cesta.
   * @private
   * @returns {FileSystemPath}
   */
  parseTempPhysicalPath(physicalPath, id) {
    const tempFilePath = FileSystemPathFactory.fromInfo({
      extension: this.temporaryExtension,
      stem: id
    });
    return FileSystemPathFactory.join(physicalPath, tempFilePath);
  }

  /**
   * Určí na jakou dočasnou fyzickou cestu rozbalit stažený archiv do složky.
   * Z každé URL se rozbaluje do unikátní cesty (mezi ostatními URL),
   * ale zároveň do stále stejné, aby se zablokovala případná paralelní
   * rozbalení archivu ze stejné URL.
   * @param {FileSystemPath} physicalPath Fyzická cesta.
   * @param {URI} uri URI adresa
   * @private
   * @returns {FileSystemPath}
   */
  parseTempDirPhysicalPath(physicalPath, uri) {
    // Vygeneruje se identifikátor pro danou URL.
    // Zároveň se předá index, protože jde o druhý dočasný soubor.
    // Tím se vygeneruje identifikátor a cesta odlišná od staženého souboru.
    const temporaryId = AssetDownloader.generateId(uri, { index: 1 });
    // Z identifikátoru se vytvoří dočasná cesta.
    return this.parseTempPhysicalPath(physicalPath, temporaryId);
  }

  /**
   * Určí na jakou dočasnou fyzickou cestu uložit stahovaný soubor.
   * Z každé URL se stahuje do unikátní cesty (mezi ostatními URL),
   * ale zároveň do stále stejné, aby se zablokovali případná paralelní
   * stahování ze stejné URL.
   * @param {FileSystemPath} physicalPath Fyzická cesta.
   * @param {URI} uri
   * @private
   * @returns {FileSystemPath}
   */
  parseTempFilePhysicalPath(physicalPath, uri) {
    // Vygeneruje se identifikátor pro danou URL.
    const temporaryId = AssetDownloader.generateId(uri);
    // Z identifikátoru se vytvoří dočasná cesta.
    return this.parseTempPhysicalPath(physicalPath, temporaryId);
  }

  /**
   * Odstraní soubor pokud existuje. V přípradě že neexistuje je chyba ignorována.
   * @param {FileSystemPath} physicalPath Fyzická cesta k souboru.
   * @private
   * @returns {Promise<void>}
   */
  removePhysicalFile(physicalPath) {
    return new Promise((resolve) =>
      resolve(
        this.fileSystemManager
          // Zkusí se, jestli existuje .tmp soubor a pokud ano, tak to znamená,
          // že předchozí stažení se násilně přerušilo a .tmp soubor tam zůstal.
          .getEntry(physicalPath)
          // Použije se smazání s recursive, protože záznamem může být i neprázdná složka.
          .then((entry) => entry.remove({ recursive: true }))
      )
    );
  }
}

/**
 * Informace o fyzickém záznamu ve složce s cachovanými soubory.
 * @typedef {Object} FileInfo
 * @property {FileSystemEntry} entry Záznam s fyzickýn souborem v souborovém systému.
 * @property {string} id Id of a model which is associated with a file.
 * @property {boolean} temporary True if file is temporary and will be moved
 * to the final destination.
 */

// TODO #555: Ideálně všechny static a private metody dát mimo třídu a toto se pak nemusí řešit.
/**
 * "Rozhraní" neboli pick veřejných metod, zejména pro AssetDownloaderMock.
 * @typedef {Pick<AssetDownloader, 'checkAssets' | 'destroy' | 'download' | 'listFile'>} IAssetDownloader
 */

/**
 * @private
 * @typedef {import('../asset').Asset<import('../../store/store').MaybeAttrs<import('../asset-attrs').AssetAttrs>>} MaybeAsset
 */

/**
 * Ověří validitu možného assetu a pro "zvalidovatelné" provede in-place modifikaci.
 * Pokud není asset validní, tak vrátí false.
 * @private
 * @param {MaybeAsset} maybeAsset Kandidát na Asset.
 * @returns {this is AssetAttrs} Vrátí true, pokud byl vstup v pořádku nebo namigrován
 * a false pokud nešel namigrovat (v tom případě vstup nebyl modifikován).
 */
function assetMakeValid(maybeAsset) {
  const maybeValid = Object.entries(
    /** @satisfies {{ [P in keyof AssetAttrs]: (value: MaybeAsset[P]) => boolean }} */ ({
      checksum: (v) => isString(v) || isNil(v),
      etag: (v) => isString(v) || isNil(v),
      id: (v) => isString(v) && v.length > 0,
      lastFetched: (v) => isISOString(v) || isNil(v),
      lastModified: (v) => isISOString(v) || isNil(v),
      path: (v) => isString(v) && v.length > 0,
      url: (v) => isString(v) && v.length > 0
    })
  ).reduce((acc, [key, validate]) => acc && validate(maybeAsset[key]), true);
  if (maybeValid) {
    Object.assign(
      maybeAsset,
      // TODO #555: Odvodit volitelné atributy automaticky,
      // protože ručně je to neudržitelné.
      // TODO: Nějak zefektivnit, aby se zbytečně nemodifikovalo
      // a taky nemohlo dojít k překlepu.
      /** @satisfies {Omit<AssetAttrs, 'id' | 'path' | 'url'>} */ ({
        checksum: maybeAsset.checksum ?? null,
        etag: maybeAsset.etag ?? null,
        lastFetched: maybeAsset.lastFetched ?? null,
        lastModified: maybeAsset.lastModified ?? null
      })
    );
  }
  return maybeValid;
}

/**
 * Načte volitelné datum v RFC 7231 formátu, tj. typicky používané v HTTP hlavičkách.
 * @param {string | null | undefined} string
 * @private
 * @returns {Date | undefined}
 */
function parseDate(string) {
  if (isNil(string) || !isString(string)) {
    return;
  }
  // TODO: Pozor, new Date() se nedoporučuje používat, protože je to
  // implementačně závislá magie (některé implementace umí detekovat určité
  // formáty a některé ne, je to závislé na locale, atd.), která navíc ani
  // nedělá validaci výsledného timestampu, tj. 29.2. nepřestupného roku
  // "zaokrouhlí" 1.3., z 32.1. udělá 1.2., atd.
  // TODO: Případně nasadit např. fecha.parse(), což ale nám vestavěnou
  // podporu pro RFC 7231, takže se musí odvodit formátovací string.
  const date = new Date(string);
  if (isNaN(date.valueOf())) {
    return;
  }
  return date;
}

/**
 * Načte datum Last-Modified z HTTP hlaviček.
 * @param {Headers} headers
 * @private
 * @returns {Date|undefined}
 */
function parseLastModified(headers) {
  return parseDate(headers.get('Last-Modified'));
}

/**
 * @param {Medium} medium
 * @returns {Headers}
 */
export function getHeadersForMedium(medium) {
  const { asset } = medium;
  const headers = new Headers();
  if (asset?.etag) {
    headers.set('If-None-Match', asset.etag);
  }
  const lastModified = parseDate(asset?.lastModified)?.toUTCString();
  if (lastModified) {
    headers.set('If-Modified-Since', lastModified);
  }

  return headers;
}
